export const productSpecificationsEn = {
  admin: {
    nav: 'Product specifications',
    title: 'Product specifications',
    description: 'Define reusable properties, schemas, declarations and versioned PDF layouts.',
  },
  tabs: {
    schemas: 'Schemas',
    properties: 'Property library',
    units: 'Units',
    pdfTemplates: 'PDF layouts',
    declarations: 'Declarations',
    settings: 'Settings',
  },
  actions: {
    create: 'Create specification',
    openRevision: 'Open revision',
    backToOverview: 'Back to overview',
    backToProduct: 'Back to product',
    preview: 'Preview',
    publish: 'Publish',
    validate: 'Validate',
    submit: 'Submit for review',
    requestChanges: 'Request changes',
    approve: 'Approve',
    withdraw: 'Withdraw',
    generatePdf: 'Generate PDF',
    download: 'Download',
    addRow: 'Add row',
    moveUp: 'Move up',
    moveDown: 'Move down',
  },
  fields: {
    name: 'Name',
    translatedName: 'Translated name',
    title: 'Title',
    text: 'Text',
    key: 'Technical key',
    type: 'Type',
    description: 'Description',
    schema: 'Schema',
    revision: 'Revision',
    version: 'Version',
    validity: 'Validity',
    validFrom: 'Valid from',
    changeReason: 'Change reason',
    reason: 'Reason',
    translationStatus: 'Translation status',
    sectionKey: 'Section key',
    sectionTitle: 'Section title',
    columns: 'Columns',
    pageBreakBefore: 'Page break before',
    required: 'Required',
    dimension: 'Dimension',
    defaultUnit: 'Default unit',
    translatable: 'Translatable',
    sensitive: 'Sensitive',
    reportable: 'Reportable',
    symbol: 'Symbol',
    decimalPlaces: 'Decimal places',
    conversionFactor: 'Conversion factor',
    conversionOffset: 'Conversion offset',
    currency: 'Currency',
    minimum: 'Minimum',
    maximum: 'Maximum',
    value: 'Value',
    unit: 'Unit',
    tolerance: 'Tolerance',
    toleranceType: 'Tolerance type',
    measurementMethod: 'Measurement method',
    remark: 'Remark',
    referenceIds: 'Internal references, comma-separated',
    declarationVersionId: 'Declaration version',
    confirmed: 'Confirmed',
    confirmationRequired: 'Confirmation required',
  },
  values: { none: 'None', required: 'Required', openEnded: 'open-ended', yes: 'Yes', no: 'No' },
  toleranceTypes: { ABSOLUTE: 'Absolute', PERCENT: 'Percentage' },
  states: {
    loadError: 'The specification data could not be loaded.',
    emptyTitle: 'No specifications yet',
    emptyDescription:
      'Create a specification from a published schema to maintain controlled product data.',
    noRevision: 'No revision is available.',
  },
  product: {
    title: 'Product specifications',
    description: 'Versioned, multilingual and approved product information.',
  },
  create: {
    title: 'Create product specification',
    description: 'Choose a published schema and define the first revision.',
    selectSchema: 'Select a schema',
    success: 'Specification created.',
    error: 'Specification could not be created.',
  },
  property: {
    title: 'Property library',
    description: 'Reusable typed definitions for every industry.',
    add: 'Add property',
    empty: 'No properties have been defined.',
    createSuccess: 'Property created.',
    createError: 'Property could not be created.',
    types: {
      SHORT_TEXT: 'Short text',
      MULTILINE_TEXT: 'Multiline text',
      RICH_TEXT: 'Safe rich text',
      INTEGER: 'Integer',
      DECIMAL: 'Decimal',
      PERCENT: 'Percentage',
      CURRENCY: 'Currency',
      BOOLEAN: 'Boolean',
      DATE: 'Date',
      DATE_TIME: 'Date and time',
      SINGLE_CHOICE: 'Single choice',
      MULTIPLE_CHOICE: 'Multiple choice',
      TRANSLATABLE_CHOICE: 'Translatable choice',
      MEASUREMENT: 'Value with unit',
      RANGE: 'Range',
      MINIMUM: 'Minimum value',
      MAXIMUM: 'Maximum value',
      TARGET_TOLERANCE: 'Target with tolerance',
      COMPARISON_MEASUREMENT: 'Measurement with operator',
      FORMULA: 'Formula',
      IMAGE: 'Image',
      IMAGE_GALLERY: 'Image gallery',
      DMS_FILE: 'DMS file',
      CERTIFICATE: 'Certificate reference',
      PRODUCT_REFERENCE: 'Product reference',
      URL: 'URL',
      KEY_VALUE_TABLE: 'Key-value table',
      CUSTOM_TABLE: 'Custom table',
      MATRIX: 'Matrix',
      LIST: 'List',
      NUMBERED_INSTRUCTIONS: 'Numbered instructions',
      DECLARATION: 'Declaration',
      SECTION_HEADING: 'Section heading',
      INFORMATION: 'Information text',
    },
    configuration: {
      SINGLE_CHOICE: 'First option',
      MULTIPLE_CHOICE: 'First option',
      TRANSLATABLE_CHOICE: 'First option',
      FORMULA: 'Formula expression',
      KEY_VALUE_TABLE: 'First column',
      CUSTOM_TABLE: 'First column',
      MATRIX: 'First column',
    },
  },
  unit: {
    title: 'Unit library',
    description: 'Tenant-specific dimensions and exact Decimal conversion rules.',
    add: 'Add unit',
    createSuccess: 'Unit created.',
    createError: 'Unit could not be created.',
  },
  schema: {
    title: 'Specification schemas',
    description: 'Compose versioned capture forms from reusable properties.',
    add: 'Create schema',
    empty: 'No schemas have been defined.',
  },
  builder: {
    library: 'Property library',
    inspector: 'Inspector',
    addProperty: 'Add {{name}}',
    dropHint: 'Drop a property here or use its add button.',
    addSection: 'Add section',
    saveSuccess: 'Schema draft saved.',
    saveError: 'Schema could not be saved.',
    versionSuccess: 'New schema version created.',
    draftAlreadyExists: 'A draft version already exists.',
    validationError: 'Check all required keys, translations and configurations.',
    publishSuccess: 'Schema published.',
    publishError: 'Schema could not be published.',
    validationIssues: '{{count}} validation issues remain.',
  },
  editor: {
    saved: 'Saved',
    unsaved: 'Save changes',
    saveError: 'Changes could not be saved. Reload before overwriting concurrent edits.',
    completeness: 'Completeness',
    contents: 'Contents',
    validationSummary: 'Validation issues',
    sourceText: 'Source text',
    language: 'Language',
    workflow: 'Review and approval',
    pdf: 'PDF documents',
    valid: 'The revision is valid.',
  },
  sources: {
    type: 'Data source',
    field: 'Source field',
    key: 'Source key',
    imageVariant: 'Image variant',
    packagingId: 'Packaging ID (optional)',
    automaticSelection: 'Select automatically',
    dmsCategory: 'DMS category',
    expression: 'Formula expression',
    market: 'Market',
    parameterKey: 'Analysis key (optional)',
    domainKey: 'Analysis domain',
    allParameters: 'All parameters as a table',
    display: 'Display',
    snapshotOnCreate: 'Freeze source on creation',
    refreshOnRevision: 'Refresh on a new revision',
    copyInitially: 'Copy initial value',
    showLiveInDraft: 'Show live source in drafts',
    readOnly: 'Read-only',
    manuallyOverridable: 'Allow reasoned override',
    refresh: 'Refresh source data',
    refreshSuccess: 'Source data and provenance were refreshed.',
    refreshError: 'Source data could not be refreshed.',
    warningTitle: 'Incomplete or contradictory source data',
    warningItem: 'Source field “{{field}}” is incomplete ({{code}}).',
    overrideReason: 'Reason for the manual override',
    overrideReasonRequired: 'A reason is required for a traceable override.',
    overrideReasonSaved: 'Saved override reason: {{reason}}',
    provenance: 'Source: {{source}} · Version: {{version}}',
    snapshot: 'frozen snapshot',
    types: {
      MANUAL: 'Manual input',
      PRODUCT_FIELD: 'Product master',
      PRODUCT_CUSTOM_FIELD: 'Custom product field',
      PRODUCT_CATEGORY: 'Product category',
      PRODUCT_IMAGE: 'Product image',
      PACKAGING: 'Packaging data',
      DMS_ATTACHMENT: 'DMS attachment',
      FORMULA: 'Formula',
      RECIPE: 'Approved recipe',
      PRODUCT_DECLARATION: 'Approved declaration',
      ALLERGEN: 'Allergen data',
      NUTRITION: 'Nutrition values',
      PROCESS_ROUTING: 'Approved process',
      YIELD: 'Yield',
      ANALYSIS: 'Analysis values',
      MATERIAL_PROFILE: 'Material master',
    },
    origins: { MANUAL: 'Manual', AUTOMATIC: 'Automatic', MANUAL_OVERRIDE: 'Manual override' },
    values: {
      articleNumber: 'Article number',
      name: 'Name',
      description: 'Description',
      ean: 'EAN',
      manufacturerNumber: 'Manufacturer number',
      weightNetKg: 'Net weight',
      weightGrossKg: 'Gross weight',
      countryOfOrigin: 'Country of origin',
      THUMBNAIL: 'Thumbnail',
      LIST: 'List view',
      CATALOG: 'Catalog view',
      DETAIL: 'Detail view',
      ALL: 'All',
      GENERAL_FILE: 'General file',
      CERTIFICATE: 'Certificate',
      DATASHEET: 'Datasheet',
      NUMBER: 'Number',
      NAME: 'Name',
      DESCRIPTION: 'Description',
      BASE_QUANTITY: 'Reference quantity',
      BASE_UNIT: 'Base unit',
      COMPONENTS: 'Components',
      INTERNAL_MATERIAL_COST: 'Internal material cost',
      DMS_ATTACHMENTS: 'DMS documents',
      PRODUCT_DESIGNATION: 'Product designation',
      INGREDIENT_TEXT: 'Ingredient list',
      ALLERGEN_TEXT: 'Allergen statement',
      ADDITIVE_TEXT: 'Additive statement',
      WARNING_TEXT: 'Warnings',
      COMBINED_TEXT: 'Combined declaration text',
      STEPS: 'Process steps',
      INSTRUCTIONS: 'Work instructions',
      QUALITY_CHECKS: 'Quality checks',
      PHOTOS: 'Process photos',
      PERCENT: 'Percent',
      FACTOR: 'Factor',
      STANDARD_UNIT: 'Standard quantity unit',
      DENSITY: 'Density',
      ORIGIN: 'Origin',
      MANUFACTURER: 'Manufacturer',
      TECHNICAL_PROPERTIES: 'Technical properties',
      QUALITY_PROPERTIES: 'Quality properties',
      STORAGE_CONDITIONS: 'Storage conditions',
      STORAGE_INSTRUCTIONS: 'Storage instructions',
      SHELF_LIFE_DAYS: 'Shelf life in days',
    },
  },
  impact: {
    title: 'Review required',
    description: 'Approved source data changed. Review the impact before the next approval.',
    badge: 'Review required',
    count: '{{count}} source changes affect this specification.',
    item: 'The “{{source}}” source changed.',
  },
  workflow: {
    submitted: 'Revision submitted for review.',
    changesRequested: 'Changes requested.',
    approved: 'Revision approved.',
    published: 'Revision published.',
    withdrawn: 'Revision withdrawn.',
    error: 'Workflow action failed.',
  },
  revisionTools: {
    title: 'Revision and schema version',
    createNext: 'Create next revision',
    created: 'A new revision was created.',
    createError: 'The new revision could not be created.',
    compare: 'Compare with revision',
    selectRevision: 'Select revision',
    changeCount: '{{count}} changes',
    noChanges: 'No differences were found.',
    changeKind: { ADDED: 'Added', REMOVED: 'Removed', CHANGED: 'Changed' },
    migration: 'Controlled schema migration',
    selectSchemaVersion: 'Select published schema version',
    migrate: 'Create migrated revision',
    migrated: 'The migrated draft revision was created.',
    migrationError: 'The revision could not be migrated.',
    migrationCategory: {
      unchanged: 'Unchanged fields',
      new: 'New fields',
      removed: 'Removed fields',
      incompatible: 'Incompatible fields',
      carriable: 'Carriable values',
      manual: 'Manual review',
    },
  },
  settings: {
    title: 'Specification settings',
    description: 'Configure tenant-wide workflow, validity and immutable PDF file naming.',
    defaultSourceLocale: 'Default source language',
    validityWarningDays: 'Validity warning (days)',
    fileNamePattern: 'PDF file name pattern',
    fileNamePatternHelp:
      'Available tokens: {{articleNumber}}, {{specificationKey}}, {{revision}}, {{locale}}.',
    requireFourEyes: 'Require four-eyes approval by default',
    creatorMayApprove: 'Allow the creator to approve',
    createPdfOnPublish: 'Create PDFs when publishing',
    saved: 'Specification settings saved.',
    saveError: 'Specification settings could not be saved.',
  },
  declaration: {
    title: 'Declarations and certificates',
    description: 'Maintain legal and industry-specific texts as configurable versions.',
    createSuccess: 'Declaration created.',
    createError: 'Declaration could not be created.',
    publishSuccess: 'Declaration published.',
    publishError: 'Declaration could not be published.',
  },
  pdf: {
    title: 'Specification PDF designer',
    description:
      'A dedicated dynamic-flow layout domain that shares the established PDF foundation.',
    blockLibrary: 'Block library',
    inspector: 'Page inspector',
    blockText: 'Translated block text',
    addBlock: 'Add block',
    pagePreview: 'Print page preview',
    createSuccess: 'PDF layout created.',
    createError: 'PDF layout could not be created.',
    publishSuccess: 'PDF layout published.',
    publishError: 'PDF layout could not be published.',
    previewError: 'PDF preview failed.',
    previewTitle: 'Specification PDF preview',
    queued: 'PDF generation queued.',
    combined: 'Combined languages',
    attachments: 'Optional DMS attachments',
    attachmentCount: '{{count}} frozen attachments',
    generateCombined: 'Combined multilingual document',
    versionCount: '{{count}} versions',
    status: { PENDING: 'Queued', PROCESSING: 'Generating', READY: 'Ready', FAILED: 'Failed' },
    blocks: {
      COMPANY_LOGO: 'Company logo',
      COMPANY_ADDRESS: 'Company address',
      DOCUMENT_TITLE: 'Document title',
      PRODUCT_NAME: 'Product name',
      ARTICLE_NUMBER: 'Article number',
      PRODUCT_CATEGORY: 'Product category',
      PRODUCT_IMAGE: 'Product image',
      IMAGE_GALLERY: 'Image gallery',
      SPECIFICATION_NUMBER: 'Specification number',
      REVISION_NUMBER: 'Revision number',
      STATUS: 'Status',
      VALIDITY_DATE: 'Validity',
      CREATED_DATE: 'Creation date',
      APPROVED_DATE: 'Approval date',
      SECTION: 'Specification section',
      PROPERTY: 'Property',
      PROPERTY_GROUP: 'Property group',
      DYNAMIC_TABLE: 'Dynamic table',
      MATRIX: 'Matrix',
      LONG_TEXT: 'Long text',
      NOTICE: 'Notice box',
      CERTIFICATE_LIST: 'Certificate list',
      DMS_FILE_LIST: 'DMS file list',
      ATTACHMENT_LIST: 'Attachment list',
      APPROVAL: 'Approval block',
      REVISION_HISTORY: 'Revision history',
      TABLE_OF_CONTENTS: 'Table of contents',
      HEADER: 'Page header',
      FOOTER: 'Page footer',
      PAGE_NUMBER: 'Page number',
      CONFIDENTIALITY: 'Confidentiality label',
      WATERMARK: 'Watermark',
      QR_CODE: 'Verification QR code',
      STATIC_TEXT: 'Static translated text',
      CONDITIONAL: 'Conditional block',
    },
  },
  status: {
    schema: { NEW: 'New', DRAFT: 'Draft', PUBLISHED: 'Published', ARCHIVED: 'Archived' },
    revision: {
      DRAFT: 'Draft',
      IN_REVIEW: 'In review',
      CHANGES_REQUESTED: 'Changes requested',
      APPROVED: 'Approved',
      PUBLISHED: 'Published',
      SUPERSEDED: 'Superseded',
      WITHDRAWN: 'Withdrawn',
    },
    translation: {
      NOT_STARTED: 'Not started',
      IN_PROGRESS: 'In progress',
      IN_REVIEW: 'In review',
      APPROVED: 'Approved',
    },
  },
  operators: {
    EQUAL: '=',
    LESS_THAN: '<',
    LESS_THAN_OR_EQUAL: '≤',
    GREATER_THAN: '>',
    GREATER_THAN_OR_EQUAL: '≥',
    RANGE: 'Range',
    APPROXIMATELY: 'Approximately',
    NOT_DETECTABLE: 'Not detectable',
    PASSED: 'Passed',
    FAILED: 'Failed',
  },
} as const;
